import React from 'react';
import './App.css';
import {
    Pivot,
    PivotItem,
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    IColumn,
    PrimaryButton,
    DefaultButton,
    TextField,
    Toggle,
    Stack,
    Text,
    Icon,
    FontWeights,
    MessageBar,
    MessageBarType,
    Dropdown,
    IDropdownOption,
    Dialog,
    DialogType,
    DialogFooter,
    IconButton,
    Spinner,
    SpinnerSize,
    DatePicker,
    DayOfWeek,
    IDatePickerStrings,
    getTheme,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { LogbookEntry } from './Model/LogbookEntry';
import { Car } from './Model/Car';
import { AdminHeader } from './Components/Admin/AdminHeader';
import { LoginCard } from './Components/Admin/LoginCard';
import { StatisticsBar } from './Components/Admin/StatisticsBar';
import { LogbookGrid } from './Components/Admin/LogbookGrid';
import { ConfirmationDialog } from './Components/Admin/ConfirmationDialog';
import { LoadingOverlay } from './Components/Admin/LoadingOverlay';
import { useAdminStyles } from './Components/Admin/adminStyles';
import { initApiService, getApiService, User, Rate } from './services/ApiService';

function Admin() {
    const navigate = useNavigate();
    const { theme, contentStyles } = useAdminStyles();
    const fluent = getTheme();

    // ── Auth ──────────────────────────────────────────────────────────────────
    const { authResponse, login, loginError, logout, apiBase } = useAuth();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    // ── Logbook state ─────────────────────────────────────────────────────────
    const [data, setData] = React.useState<LogbookEntry[]>([]);
    const [showAllRides, setShowAllRides] = React.useState<boolean>(false);
    const [isDialogVisible, setIsDialogVisible] = React.useState(false);
    const [dialogContent, setDialogContent] = React.useState({ id: '', finalPrice: 0 });
    const [dialogResolve, setDialogResolve] = React.useState<(v: boolean) => void>(() => () => {});

    // ── Filter state ──────────────────────────────────────────────────────────
    const [filterDriver, setFilterDriver] = React.useState<string>('');
    const [filterCar, setFilterCar] = React.useState<string>('');
    const [filterFrom, setFilterFrom] = React.useState<Date | undefined>(undefined);
    const [filterTo, setFilterTo] = React.useState<Date | undefined>(undefined);

    const datePickerStrings: IDatePickerStrings = {
        months: ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'],
        shortMonths: ['Led','Úno','Bře','Dub','Kvě','Čer','Čvc','Srp','Zář','Říj','Lis','Pro'],
        days: ['Neděle','Pondělí','Úterý','Středa','Čtvrtek','Pátek','Sobota'],
        shortDays: ['Ne','Po','Út','St','Čt','Pá','So'],
        goToToday: 'Přejít na dnešek',
        prevMonthAriaLabel: 'Předchozí měsíc',
        nextMonthAriaLabel: 'Následující měsíc',
        prevYearAriaLabel: 'Předchozí rok',
        nextYearAriaLabel: 'Následující rok',
    };

    const formatDateForApi = (d: Date) => d.toISOString().split('T')[0];
    const formatDateLabel = (d?: Date) => d ? d.toLocaleDateString('cs-CZ') : '';

    // ── Cars state ────────────────────────────────────────────────────────────
    const [cars, setCars] = React.useState<Car[]>([]);
    const [newCarName, setNewCarName] = React.useState('');
    const [newCarPlate, setNewCarPlate] = React.useState('');
    const [newCarKm, setNewCarKm] = React.useState('');
    const [addingCar, setAddingCar] = React.useState(false);
    const [editCarId, setEditCarId] = React.useState<string | null>(null);
    const [editKm, setEditKm] = React.useState('');
    const [carError, setCarError] = React.useState<string | null>(null);

    // ── Users state ───────────────────────────────────────────────────────────
    const [users, setUsers] = React.useState<User[]>([]);
    const [newUserEmail, setNewUserEmail] = React.useState('');
    const [newUserDisplayName, setNewUserDisplayName] = React.useState('');
    const [newUserAdmin, setNewUserAdmin] = React.useState(false);
    const [addingUser, setAddingUser] = React.useState(false);
    const [userError, setUserError] = React.useState<string | null>(null);
    const [deleteConfirmUser, setDeleteConfirmUser] = React.useState<string | null>(null);
    const [adminToggleConfirmUser, setAdminToggleConfirmUser] = React.useState<User | null>(null);
    const [editDisplayNameEmail, setEditDisplayNameEmail] = React.useState<string | null>(null);
    const [editDisplayNameValue, setEditDisplayNameValue] = React.useState('');

    // ── Rates state ───────────────────────────────────────────────────────────
    const [rates, setRates] = React.useState<Rate[]>([]);
    const [newRatePurpose, setNewRatePurpose] = React.useState('');
    const [newRateDayRate, setNewRateDayRate] = React.useState('');
    const [newRateNightRate, setNewRateNightRate] = React.useState('');
    const [newRateType, setNewRateType] = React.useState<'Taxi' | 'Service' | 'Personal' | 'Contract'>('Taxi');
    const [addingRate, setAddingRate] = React.useState(false);
    const [rateError, setRateError] = React.useState<string | null>(null);
    const [deleteConfirmRate, setDeleteConfirmRate] = React.useState<string | null>(null);
    const [editRateId, setEditRateId] = React.useState<string | null>(null);
    const [editRatePurpose, setEditRatePurpose] = React.useState('');
    const [editRateDayRate, setEditRateDayRate] = React.useState('');
    const [editRateNightRate, setEditRateNightRate] = React.useState('');
    const [editRateType, setEditRateType] = React.useState<'Taxi' | 'Service' | 'Personal' | 'Contract'>('Taxi');

    // ── Init ──────────────────────────────────────────────────────────────────
    React.useEffect(() => {
        if (!authResponse?.token) return;
        initApiService(apiBase, authResponse.token, logout);
        Promise.all([fetchEntries(), fetchCars(), fetchUsers(), fetchRates()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authResponse?.token]);

    async function fetchEntries(filters?: { driverId?: string; carId?: string; from?: string; to?: string }) {
        try {
            setLoading(true);
            const entries = await getApiService().getLogbookEntries(filters);
            setData(entries);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Chyba při načítání jízd');
        } finally {
            setLoading(false);
        }
    }

    async function applyFilters() {
        await fetchEntries({
            driverId: filterDriver || undefined,
            carId: filterCar || undefined,
            from: filterFrom ? formatDateForApi(filterFrom) : undefined,
            to: filterTo ? formatDateForApi(filterTo) : undefined,
        });
    }

    async function clearFilters() {
        setFilterDriver('');
        setFilterCar('');
        setFilterFrom(undefined);
        setFilterTo(undefined);
        await fetchEntries();
    }

    function openConfirmationForRowCheck(id: string, finalPrice: number) {
        return new Promise<boolean>((resolve) => {
            setDialogContent({ id, finalPrice });
            setDialogResolve(() => resolve);
            setIsDialogVisible(true);
        });
    }

    async function handleCheckboxChange(item: LogbookEntry, _checked: boolean) {
        const confirmed = await openConfirmationForRowCheck(item.envelope, item.finalPrice);
        if (confirmed) {
            try {
                await getApiService().markEntryChecked(item.id, true);
                setData(prev => prev.map(x => x.id === item.id ? { ...x, rowChecked: true } : x));
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Chyba při aktualizaci');
            }
        }
    }

    // ── Cars ──────────────────────────────────────────────────────────────────
    async function fetchCars() {
        try {
            const c = await getApiService().getCars();
            setCars(c);
        } catch (e) {
            // cars are non-critical on initial load
        }
    }

    async function handleAddCar() {
        if (!newCarName.trim() || !newCarPlate.trim()) {
            setCarError('Vyplňte název i SPZ.');
            return;
        }
        const km = newCarKm.trim() === '' ? 0 : parseInt(newCarKm, 10);
        if (isNaN(km) || km < 0) {
            setCarError('Neplatná hodnota kilometrů.');
            return;
        }
        try {
            setAddingCar(true);
            setCarError(null);
            const car = await getApiService().createCar({ carName: newCarName.trim(), licensePlate: newCarPlate.trim(), kilometers: km });
            setCars(prev => [...prev, car]);
            setNewCarName('');
            setNewCarPlate('');
            setNewCarKm('');
        } catch (e) {
            setCarError(e instanceof Error ? e.message : 'Chyba při přidávání vozidla');
        } finally {
            setAddingCar(false);
        }
    }

    async function handleDeleteCar(id: string) {
        try {
            await getApiService().deleteCar(id);
            setCars(prev => prev.filter(c => c.rowKey !== id));
        } catch (e) {
            setCarError(e instanceof Error ? e.message : 'Chyba při mazání vozidla');
        }
    }

    async function handleUpdateKm(car: Car) {
        const km = parseInt(editKm, 10);
        if (isNaN(km) || km < 0) {
            setCarError('Neplatná hodnota kilometrů.');
            return;
        }
        try {
            setCarError(null);
            const updated = await getApiService().updateCar(car.rowKey, { ...car, kilometers: km });
            setCars(prev => prev.map(c => c.rowKey === car.rowKey ? updated : c));
            setEditCarId(null);
            setEditKm('');
        } catch (e) {
            setCarError(e instanceof Error ? e.message : 'Chyba při aktualizaci km');
        }
    }

    // ── Users ─────────────────────────────────────────────────────────────────
    async function fetchUsers() {
        try {
            const u = await getApiService().getUsers();
            setUsers(u);
        } catch (e) {
            // may fail if current user is not admin — silently ignore
        }
    }

    const [newUserPassword, setNewUserPassword] = React.useState('');

    async function handleAddUser() {
        if (!newUserEmail.trim()) {
            setUserError('Zadejte e-mail.');
            return;
        }
        if (!newUserPassword.trim()) {
            setUserError('Zadejte heslo.');
            return;
        }
        try {
            setAddingUser(true);
            setUserError(null);
            const user = await getApiService().createUser(newUserEmail.trim(), newUserAdmin, newUserPassword, newUserDisplayName.trim() || undefined);
            setUsers(prev => [...prev, user]);
            setNewUserEmail('');
            setNewUserDisplayName('');
            setNewUserAdmin(false);
            setNewUserPassword('');
        } catch (e) {
            setUserError(e instanceof Error ? e.message : 'Chyba při přidávání uživatele');
        } finally {
            setAddingUser(false);
        }
    }

    async function handleToggleAdmin(user: User) {
        setAdminToggleConfirmUser(user);
    }

    async function confirmToggleAdmin() {
        if (!adminToggleConfirmUser) return;
        const user = adminToggleConfirmUser;
        setAdminToggleConfirmUser(null);
        try {
            const updated = await getApiService().updateUser(user.email, !user.isAdmin, user.displayName);
            setUsers(prev => prev.map(u => u.email === user.email ? updated : u));
        } catch (e) {
            setUserError(e instanceof Error ? e.message : 'Chyba při aktualizaci');
        }
    }

    async function handleDeleteUser(email: string) {
        try {
            await getApiService().deleteUser(email);
            setUsers(prev => prev.filter(u => u.email !== email));
            setDeleteConfirmUser(null);
        } catch (e) {
            setUserError(e instanceof Error ? e.message : 'Chyba při mazání uživatele');
        }
    }

    function startEditDisplayName(user: User) {
        setEditDisplayNameEmail(user.email);
        setEditDisplayNameValue(user.displayName ?? '');
    }

    function cancelEditDisplayName() {
        setEditDisplayNameEmail(null);
        setEditDisplayNameValue('');
    }

    async function saveDisplayName() {
        if (!editDisplayNameEmail) return;
        const user = users.find(u => u.email === editDisplayNameEmail);
        if (!user) return;
        try {
            const updated = await getApiService().updateUser(user.email, user.isAdmin, editDisplayNameValue.trim() || undefined);
            setUsers(prev => prev.map(u => u.email === user.email ? updated : u));
            setEditDisplayNameEmail(null);
            setEditDisplayNameValue('');
        } catch (e) {
            setUserError(e instanceof Error ? e.message : 'Chyba při aktualizaci');
        }
    }

    // ── Rates ─────────────────────────────────────────────────────────────────
    async function fetchRates() {
        try {
            const r = await getApiService().getRates();
            setRates(r);
        } catch (e) {
            // non-critical on initial load
        }
    }

    async function handleAddRate() {
        if (!newRatePurpose.trim()) {
            setRateError('Zadejte název účelu.');
            return;
        }
        const dayRate = parseFloat(newRateDayRate);
        const nightRate = parseFloat(newRateNightRate);
        if (newRateType === 'Taxi' && (isNaN(dayRate) || isNaN(nightRate))) {
            setRateError('Zadejte platné sazby.');
            return;
        }
        try {
            setAddingRate(true);
            setRateError(null);
            const created = await getApiService().createRate({
                purpose: newRatePurpose.trim(),
                dayRate: newRateType === 'Taxi' ? dayRate : 0,
                nightRate: newRateType === 'Taxi' ? nightRate : 0,
                rateType: newRateType,
            });
            setRates(prev => [...prev, created]);
            setNewRatePurpose('');
            setNewRateDayRate('');
            setNewRateNightRate('');
            setNewRateType('Taxi');
        } catch (e) {
            setRateError(e instanceof Error ? e.message : 'Chyba při přidávání sazby');
        } finally {
            setAddingRate(false);
        }
    }

    function startEditRate(rate: Rate) {
        setEditRateId(rate.id);
        setEditRatePurpose(rate.purpose);
        setEditRateDayRate(rate.dayRate.toString());
        setEditRateNightRate(rate.nightRate.toString());
        setEditRateType(rate.rateType);
    }

    function cancelEditRate() {
        setEditRateId(null);
    }

    async function handleSaveRate(id: string) {
        const purpose = editRatePurpose.trim();
        if (!purpose) { setRateError('Zadejte název účelu.'); return; }
        const dayRate = parseFloat(editRateDayRate);
        const nightRate = parseFloat(editRateNightRate);
        if (editRateType === 'Taxi' && (isNaN(dayRate) || isNaN(nightRate) || dayRate < 0 || nightRate < 0)) {
            setRateError('Zadejte platné sazby.'); return;
        }
        try {
            setRateError(null);
            const updated = await getApiService().updateRate(id, {
                purpose,
                dayRate: editRateType === 'Taxi' ? dayRate : 0,
                nightRate: editRateType === 'Taxi' ? nightRate : 0,
                rateType: editRateType,
            });
            setRates(prev => prev.map(r => r.id === id ? { ...updated, sortOrder: r.sortOrder } : r));
            setEditRateId(null);
        } catch (e) {
            setRateError(e instanceof Error ? e.message : 'Chyba při aktualizaci sazby');
        }
    }

    async function handleDeleteRate(id: string) {
        try {
            await getApiService().deleteRate(id);
            setRates(prev => prev.filter(r => r.id !== id));
            setDeleteConfirmRate(null);
        } catch (e) {
            setRateError(e instanceof Error ? e.message : 'Chyba při mazání sazby');
        }
    }

    async function handleMoveRate(id: string, direction: 'up' | 'down') {
        // Normalise to contiguous 0..n-1 positions based on current display order,
        // then swap the target item with its neighbour.
        const normalised = rates.map((r, i) => ({ ...r, sortOrder: i }));
        const idx = normalised.findIndex(r => r.id === id);
        if (idx < 0) return;
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= normalised.length) return;

        const reordered = [...normalised];
        [reordered[idx], reordered[targetIdx]] = [reordered[targetIdx], reordered[idx]];
        const withNewOrders = reordered.map((r, i) => ({ ...r, sortOrder: i }));

        setRates(withNewOrders); // optimistic update
        try {
            await getApiService().reorderRates(withNewOrders.map(r => ({ id: r.id, sortOrder: r.sortOrder })));
        } catch (e) {
            setRateError(e instanceof Error ? e.message : 'Chyba při změně pořadí');
            setRates(rates); // rollback
        }
    }

    // ── Derived data ──────────────────────────────────────────────────────────
    const displayedEntries = showAllRides ? data : data.filter(x => !x.rowChecked);
    const checkedEntries = data.filter(x => x.rowChecked);

    const driverOptions: IDropdownOption[] = [
        { key: '', text: 'Všichni řidiči' },
        ...Array.from(new Set(data.map(e => e.driverId)))
            .filter(Boolean)
            .map(d => ({ key: d, text: d })),
    ];

    const carOptions: IDropdownOption[] = [
        { key: '', text: 'Všechna vozidla' },
        ...cars.map(c => ({ key: c.rowKey, text: c.carName || c.licensePlate })),
    ];

    // ── Car columns ───────────────────────────────────────────────────────────
    const carColumns: IColumn[] = [
        { key: 'name', name: 'Název', fieldName: 'carName', minWidth: 120, maxWidth: 200, isResizable: true },
        { key: 'plate', name: 'SPZ', fieldName: 'licensePlate', minWidth: 80, maxWidth: 120, isResizable: true },
        {
            key: 'km', name: 'Km', minWidth: 120, maxWidth: 180, isResizable: true,
            onRender: (item: Car) =>
                editCarId === item.rowKey ? (
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <TextField
                            value={editKm}
                            onChange={(_, v) => setEditKm(v || '')}
                            type="number"
                            styles={{ root: { width: 80 } }}
                        />
                        <IconButton iconProps={{ iconName: 'CheckMark' }} onClick={() => handleUpdateKm(item)} />
                        <IconButton iconProps={{ iconName: 'Cancel' }} onClick={() => setEditCarId(null)} />
                    </Stack>
                ) : (
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <Text>{item.kilometers.toLocaleString('cs-CZ')} km</Text>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title="Upravit km"
                            onClick={() => { setEditCarId(item.rowKey); setEditKm(item.kilometers.toString()); }}
                            styles={{ root: { height: 24 } }}
                        />
                    </Stack>
                ),
        },
        {
            key: 'del', name: '', minWidth: 40, maxWidth: 40,
            onRender: (item: Car) => (
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    title="Smazat vozidlo"
                    styles={{ root: { color: fluent.palette.redDark } }}
                    onClick={() => window.confirm(`Smazat vozidlo ${item.carName}?`) && handleDeleteCar(item.rowKey)}
                />
            ),
        },
    ];

    // ── User columns ──────────────────────────────────────────────────────────
    const userColumns: IColumn[] = [
        { key: 'email', name: 'E-mail', fieldName: 'email', minWidth: 150, maxWidth: 280, isResizable: true },
        {
            key: 'displayName', name: 'Zobrazované jméno', minWidth: 160, maxWidth: 320, isResizable: true,
            onRender: (item: User) =>
                editDisplayNameEmail === item.email ? (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                        <TextField
                            value={editDisplayNameValue}
                            onChange={(_, v) => setEditDisplayNameValue(v ?? '')}
                            placeholder="Přezdívka nebo jméno"
                            styles={{ root: { flexGrow: 1, minWidth: 100 } }}
                            onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(); if (e.key === 'Escape') cancelEditDisplayName(); }}
                            autoFocus
                        />
                        <IconButton iconProps={{ iconName: 'CheckMark' }} title="Uložit" onClick={saveDisplayName} styles={{ root: { height: 28, color: fluent.palette.green } }} />
                        <IconButton iconProps={{ iconName: 'Cancel' }} title="Zrušit" onClick={cancelEditDisplayName} styles={{ root: { height: 28 } }} />
                    </Stack>
                ) : (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                        <Text style={{ color: item.displayName ? undefined : fluent.palette.neutralTertiary, fontStyle: item.displayName ? 'normal' : 'italic' }}>
                            {item.displayName || 'Nenastaveno'}
                        </Text>
                        <IconButton iconProps={{ iconName: 'Edit' }} title="Upravit zobrazované jméno" onClick={() => startEditDisplayName(item)} styles={{ root: { height: 24 } }} />
                    </Stack>
                ),
        },
        {
            key: 'admin', name: 'Admin', minWidth: 80, maxWidth: 100,
            onRender: (item: User) => (
                <Toggle
                    checked={item.isAdmin}
                    onChange={() => handleToggleAdmin(item)}
                    styles={{ root: { margin: 0 } }}
                />
            ),
        },
        {
            key: 'del', name: '', minWidth: 40, maxWidth: 40,
            onRender: (item: User) => (
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    title="Smazat uživatele"
                    styles={{ root: { color: fluent.palette.redDark } }}
                    onClick={() => setDeleteConfirmUser(item.email)}
                />
            ),
        },
    ];

    // ── Rate type options ──────────────────────────────────────────────────────
    const rateTypeOptions: IDropdownOption[] = [
        { key: 'Taxi',     text: 'Taxi' },
        { key: 'Service',  text: 'Service' },
        { key: 'Personal', text: 'Personal' },
        { key: 'Contract', text: 'Contract' },
    ];

    // ── Rate columns ──────────────────────────────────────────────────────────
    const rateColumns: IColumn[] = [
        {
            key: 'order', name: 'Pořadí', minWidth: 64, maxWidth: 64,
            onRender: (item: Rate) => {
                const idx = rates.findIndex(r => r.id === item.id);
                return (
                    <Stack horizontal tokens={{ childrenGap: 2 }} verticalAlign="center">
                        <IconButton
                            iconProps={{ iconName: 'ChevronUp' }}
                            title="Posunout nahoru"
                            disabled={idx <= 0}
                            styles={{ root: { height: 24, width: 24 } }}
                            onClick={() => handleMoveRate(item.id, 'up')}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ChevronDown' }}
                            title="Posunout dolů"
                            disabled={idx >= rates.length - 1}
                            styles={{ root: { height: 24, width: 24 } }}
                            onClick={() => handleMoveRate(item.id, 'down')}
                        />
                    </Stack>
                );
            },
        },
        {
            key: 'purpose', name: 'Účel', minWidth: 120, maxWidth: 200, isResizable: true,
            onRender: (item: Rate) =>
                editRateId === item.id
                    ? <TextField value={editRatePurpose} onChange={(_, v) => setEditRatePurpose(v || '')} styles={{ root: { width: '100%' } }} />
                    : <Text>{item.purpose}</Text>,
        },
        {
            key: 'rateType', name: 'Typ', minWidth: 110, maxWidth: 140, isResizable: true,
            onRender: (item: Rate) =>
                editRateId === item.id
                    ? <Dropdown
                        options={rateTypeOptions}
                        selectedKey={editRateType}
                        onChange={(_, o) => setEditRateType((o?.key as 'Taxi' | 'Service' | 'Personal' | 'Contract') ?? 'Taxi')}
                        styles={{ root: { minWidth: 110 } }}
                      />
                    : <Text>{item.rateType}</Text>,
        },
        {
            key: 'day', name: 'Den (Kč/km)', minWidth: 100, maxWidth: 140, isResizable: true,
            onRender: (item: Rate) =>
                editRateId === item.id
                    ? <TextField value={editRateDayRate} onChange={(_, v) => setEditRateDayRate(v || '')} type="number" disabled={editRateType !== 'Taxi'} styles={{ root: { width: 80 } }} />
                    : <Text>{item.rateType === 'Taxi' ? item.dayRate.toFixed(2) : '—'}</Text>,
        },
        {
            key: 'night', name: 'Noc (Kč/km)', minWidth: 100, maxWidth: 140, isResizable: true,
            onRender: (item: Rate) =>
                editRateId === item.id
                    ? <TextField value={editRateNightRate} onChange={(_, v) => setEditRateNightRate(v || '')} type="number" disabled={editRateType !== 'Taxi'} styles={{ root: { width: 80 } }} />
                    : <Text>{item.rateType === 'Taxi' ? item.nightRate.toFixed(2) : '—'}</Text>,
        },
        {
            key: 'actions', name: '', minWidth: 80, maxWidth: 80,
            onRender: (item: Rate) =>
                editRateId === item.id ? (
                    <Stack horizontal tokens={{ childrenGap: 4 }}>
                        <IconButton iconProps={{ iconName: 'CheckMark' }} title="Uložit" onClick={() => handleSaveRate(item.id)} />
                        <IconButton iconProps={{ iconName: 'Cancel' }} title="Zrušit" onClick={cancelEditRate} />
                    </Stack>
                ) : (
                    <Stack horizontal tokens={{ childrenGap: 4 }}>
                        <IconButton iconProps={{ iconName: 'Edit' }} title="Upravit sazbu" onClick={() => startEditRate(item)} styles={{ root: { height: 24 } }} />
                        <IconButton iconProps={{ iconName: 'Delete' }} title="Smazat sazbu" styles={{ root: { color: fluent.palette.redDark } }} onClick={() => setDeleteConfirmRate(item.id)} />
                    </Stack>
                ),
        },
    ];

    const addFormStyle: React.CSSProperties = {
        background: fluent.palette.white,
        borderRadius: 10,
        padding: '20px 24px',
        borderLeft: `4px solid ${fluent.palette.themePrimary}`,
        boxShadow: fluent.effects.elevation4,
        marginTop: 16,
        marginBottom: 12,
    };

    const listCardStyle: React.CSSProperties = {
        background: fluent.palette.white,
        borderRadius: 10,
        padding: '20px 24px',
        boxShadow: fluent.effects.elevation4,
    };

    const countBadgeStyle: React.CSSProperties = {
        background: fluent.palette.themeLighter,
        color: fluent.palette.themeDark,
        borderRadius: 20,
        padding: '2px 10px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '20px',
    };

    return (
        <div className={contentStyles.mainContainer}>
            <LoadingOverlay isVisible={loading} theme={theme} styles={contentStyles} />

            <AdminHeader
                authResponse={authResponse}
                onLogoClick={() => navigate('/')}
                onLogout={logout}
                theme={theme}
                styles={contentStyles}
            />

            <div className={contentStyles.contentArea}>
                {error && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)} style={{ marginBottom: 16 }}>
                        {error}
                    </MessageBar>
                )}

                {authResponse == null && (
                    <LoginCard onLogin={login} loginError={loginError} styles={contentStyles} />
                )}

                {authResponse !== null && !authResponse.isAdmin && (
                    <Stack
                        horizontalAlign="center"
                        verticalAlign="center"
                        tokens={{ childrenGap: 20 }}
                        styles={{
                            root: {
                                maxWidth: 420,
                                margin: '60px auto',
                                background: fluent.palette.white,
                                borderRadius: 16,
                                padding: '48px 36px',
                                boxShadow: fluent.effects.elevation8,
                                textAlign: 'center',
                            },
                        }}
                    >
                        <Icon
                            iconName="Lock"
                            styles={{
                                root: {
                                    fontSize: 48,
                                    color: fluent.palette.neutralTertiary,
                                },
                            }}
                        />
                        <Text variant="xLarge" styles={{ root: { fontWeight: FontWeights.semibold } }}>
                            Přístup odepřen
                        </Text>
                        <Text variant="medium" styles={{ root: { color: fluent.palette.neutralSecondary } }}>
                            Pro přístup do administrace je vyžadováno administrátorské oprávnění. Pokud si myslíte, že byste měli mít přístup, kontaktujte svého správce.
                        </Text>
                        <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 8 } }}>
                            <PrimaryButton
                                text="Zpět na hlavní stránku"
                                iconProps={{ iconName: 'Home' }}
                                onClick={() => navigate('/')}
                            />
                            <DefaultButton
                                text="Odhlásit se"
                                iconProps={{ iconName: 'SignOut' }}
                                onClick={logout}
                            />
                        </Stack>
                    </Stack>
                )}

                {authResponse !== null && authResponse.isAdmin && (
                    <Pivot styles={{ root: { marginBottom: 16 } }}>

                        {/* ── Jízdy ─────────────────────────────────────────── */}
                        <PivotItem headerText="Jízdy" itemIcon="Car">

                            {/* Filter card */}
                            <div style={addFormStyle}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 16 }}>
                                    <Icon iconName="Filter" style={{ fontSize: 16, color: fluent.palette.themePrimary }} />
                                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Filtry</Text>
                                </Stack>
                                <Stack tokens={{ childrenGap: 16 }}>
                                    {/* Row 1: dropdowns */}
                                    <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="end">
                                        <Dropdown
                                            label="Řidič"
                                            options={driverOptions}
                                            selectedKey={filterDriver}
                                            onChange={(_, o) => setFilterDriver(o?.key as string ?? '')}
                                            placeholder="Všichni řidiči"
                                            styles={{ root: { flex: '1 1 200px' } }}
                                        />
                                        <Dropdown
                                            label="Vozidlo"
                                            options={carOptions}
                                            selectedKey={filterCar}
                                            onChange={(_, o) => setFilterCar(o?.key as string ?? '')}
                                            placeholder="Všechna vozidla"
                                            styles={{ root: { flex: '1 1 200px' } }}
                                        />
                                    </Stack>
                                    {/* Row 2: date pickers */}
                                    <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="end">
                                        <DatePicker
                                            label="Od"
                                            value={filterFrom}
                                            onSelectDate={d => setFilterFrom(d ?? undefined)}
                                            formatDate={formatDateLabel}
                                            strings={datePickerStrings}
                                            firstDayOfWeek={DayOfWeek.Monday}
                                            placeholder="Vyberte datum..."
                                            styles={{ root: { flex: '1 1 200px' } }}
                                            allowTextInput
                                        />
                                        <DatePicker
                                            label="Do"
                                            value={filterTo}
                                            onSelectDate={d => setFilterTo(d ?? undefined)}
                                            formatDate={formatDateLabel}
                                            strings={datePickerStrings}
                                            firstDayOfWeek={DayOfWeek.Monday}
                                            placeholder="Vyberte datum..."
                                            styles={{ root: { flex: '1 1 200px' } }}
                                            allowTextInput
                                        />
                                    </Stack>
                                    {/* Row 3: actions */}
                                    <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
                                        <DefaultButton text="Vymazat filtry" iconProps={{ iconName: 'ClearFilter' }} onClick={clearFilters} />
                                        <PrimaryButton text="Filtrovat" iconProps={{ iconName: 'Search' }} onClick={applyFilters} />
                                    </Stack>
                                </Stack>
                            </div>

                            <StatisticsBar
                                displayedEntries={displayedEntries}
                                checkedEntries={checkedEntries}
                                showAllRides={showAllRides}
                                loading={loading}
                                onToggleView={setShowAllRides}
                                onRefresh={fetchEntries}
                                theme={theme}
                                styles={contentStyles}
                            />

                            <LogbookGrid
                                entries={displayedEntries}
                                showAllRides={showAllRides}
                                onCheckboxChange={handleCheckboxChange}
                                theme={theme}
                                styles={contentStyles}
                                rates={rates}
                                users={users}
                            />
                        </PivotItem>

                        {/* ── Auta ──────────────────────────────────────────── */}
                        <PivotItem headerText="Auta" itemIcon="Car">

                            {/* Add form */}
                            <div style={addFormStyle}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 16 }}>
                                    <Icon iconName="Car" style={{ fontSize: 16, color: fluent.palette.themePrimary }} />
                                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Nové vozidlo</Text>
                                </Stack>

                                {carError && (
                                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setCarError(null)} style={{ marginBottom: 12 }}>
                                        {carError}
                                    </MessageBar>
                                )}

                                <Stack tokens={{ childrenGap: 16 }}>
                                    <Stack horizontal tokens={{ childrenGap: 24 }} wrap>
                                        <TextField
                                            label="Název vozidla"
                                            value={newCarName}
                                            onChange={(_, v) => setNewCarName(v || '')}
                                            placeholder="Toyota Yaris"
                                            styles={{ root: { flex: '2 1 180px' } }}
                                        />
                                        <TextField
                                            label="SPZ"
                                            value={newCarPlate}
                                            onChange={(_, v) => setNewCarPlate(v || '')}
                                            placeholder="1AB 1234"
                                            styles={{ root: { flex: '1 1 130px' } }}
                                        />
                                        <TextField
                                            label="Počáteční km"
                                            value={newCarKm}
                                            onChange={(_, v) => setNewCarKm(v || '')}
                                            placeholder="0"
                                            type="number"
                                            suffix="km"
                                            styles={{ root: { flex: '1 1 120px' } }}
                                        />
                                    </Stack>
                                    <Stack horizontal horizontalAlign="end">
                                        <PrimaryButton
                                            text={addingCar ? 'Přidávám…' : 'Přidat vozidlo'}
                                            iconProps={addingCar ? undefined : { iconName: 'Add' }}
                                            onRenderText={addingCar ? () => (
                                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                                    <Spinner size={SpinnerSize.small} />
                                                    <span>Přidávám…</span>
                                                </Stack>
                                            ) : undefined}
                                            onClick={handleAddCar}
                                            disabled={addingCar}
                                            styles={{ root: { minWidth: 150 } }}
                                        />
                                    </Stack>
                                </Stack>
                            </div>

                            {/* List card */}
                            <div style={listCardStyle}>
                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: 12 }}>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Vozidla</Text>
                                        <span style={countBadgeStyle}>{cars.length}</span>
                                    </Stack>
                                    <DefaultButton
                                        text="Obnovit"
                                        iconProps={{ iconName: 'Refresh' }}
                                        onClick={fetchCars}
                                    />
                                </Stack>

                                <DetailsList
                                    items={cars}
                                    columns={carColumns}
                                    layoutMode={DetailsListLayoutMode.justified}
                                    selectionMode={SelectionMode.none}
                                    isHeaderVisible={true}
                                    getKey={(item: Car) => item.rowKey}
                                />
                                {cars.length === 0 && !loading && (
                                    <Stack verticalAlign="center" horizontalAlign="center" style={{ padding: '32px 0' }} tokens={{ childrenGap: 8 }}>
                                        <Icon iconName="Car" style={{ fontSize: 32, color: fluent.palette.neutralTertiaryAlt }} />
                                        <Text style={{ color: fluent.palette.neutralSecondary }}>Žádná vozidla</Text>
                                    </Stack>
                                )}
                            </div>
                        </PivotItem>

                        {/* ── Uživatelé ─────────────────────────────────────── */}
                        <PivotItem headerText="Uživatelé" itemIcon="People">

                            {/* Add form */}
                            <div style={addFormStyle}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 16 }}>
                                    <Icon iconName="AddFriend" style={{ fontSize: 16, color: fluent.palette.themePrimary }} />
                                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Nový uživatel</Text>
                                </Stack>

                                {userError && (
                                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setUserError(null)} style={{ marginBottom: 12 }}>
                                        {userError}
                                    </MessageBar>
                                )}

                                <Stack tokens={{ childrenGap: 16 }}>
                                    <TextField
                                        label="E-mail"
                                        value={newUserEmail}
                                        onChange={(_, v) => setNewUserEmail(v || '')}
                                        placeholder="jmeno@taxisever.cz"
                                        iconProps={{ iconName: 'Mail' }}
                                        styles={{ root: { maxWidth: 420 } }}
                                    />
                                    <TextField
                                        label="Zobrazované jméno"
                                        value={newUserDisplayName}
                                        onChange={(_, v) => setNewUserDisplayName(v || '')}
                                        placeholder="Přezdívka nebo jméno (nepovinné)"
                                        iconProps={{ iconName: 'Contact' }}
                                        styles={{ root: { maxWidth: 420 } }}
                                    />
                                    <TextField
                                        label="Heslo"
                                        type="password"
                                        value={newUserPassword}
                                        onChange={(_, v) => setNewUserPassword(v || '')}
                                        canRevealPassword
                                        placeholder="Nastavení hesla pro nového uživatele"
                                        styles={{ root: { maxWidth: 420 } }}
                                    />
                                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                        <Toggle
                                            label="Admin oprávnění"
                                            inlineLabel
                                            checked={newUserAdmin}
                                            onChange={(_, v) => setNewUserAdmin(v ?? false)}
                                            styles={{ root: { margin: 0 } }}
                                            onText="Ano"
                                            offText="Ne"
                                        />
                                        <PrimaryButton
                                            text={addingUser ? 'Přidávám…' : 'Přidat uživatele'}
                                            iconProps={addingUser ? undefined : { iconName: 'Add' }}
                                            onRenderText={addingUser ? () => (
                                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                                    <Spinner size={SpinnerSize.small} />
                                                    <span>Přidávám…</span>
                                                </Stack>
                                            ) : undefined}
                                            onClick={handleAddUser}
                                            disabled={addingUser}
                                            styles={{ root: { minWidth: 160 } }}
                                        />
                                    </Stack>
                                </Stack>
                            </div>

                            {/* List card */}
                            <div style={listCardStyle}>
                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: 12 }}>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Uživatelé</Text>
                                        <span style={countBadgeStyle}>{users.length}</span>
                                    </Stack>
                                    <DefaultButton
                                        text="Obnovit"
                                        iconProps={{ iconName: 'Refresh' }}
                                        onClick={fetchUsers}
                                    />
                                </Stack>

                                <DetailsList
                                    items={users}
                                    columns={userColumns}
                                    layoutMode={DetailsListLayoutMode.justified}
                                    selectionMode={SelectionMode.none}
                                    isHeaderVisible={true}
                                    getKey={(item: User) => item.email}
                                />
                                {users.length === 0 && !loading && (
                                    <Stack verticalAlign="center" horizontalAlign="center" style={{ padding: '32px 0' }} tokens={{ childrenGap: 8 }}>
                                        <Icon iconName="People" style={{ fontSize: 32, color: fluent.palette.neutralTertiaryAlt }} />
                                        <Text style={{ color: fluent.palette.neutralSecondary }}>Žádní uživatelé</Text>
                                    </Stack>
                                )}
                            </div>
                        </PivotItem>

                        {/* ── Sazby ─────────────────────────────────────────── */}
                        <PivotItem headerText="Sazby" itemIcon="Money">

                            {/* Add form */}
                            <div style={addFormStyle}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 16 }}>
                                    <Icon iconName="Money" style={{ fontSize: 16, color: fluent.palette.themePrimary }} />
                                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Nová sazba</Text>
                                </Stack>

                                {rateError && (
                                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setRateError(null)} style={{ marginBottom: 12 }}>
                                        {rateError}
                                    </MessageBar>
                                )}

                                <Stack tokens={{ childrenGap: 16 }}>
                                    <Stack horizontal tokens={{ childrenGap: 24 }} wrap>
                                        <TextField
                                            label="Název účelu"
                                            value={newRatePurpose}
                                            onChange={(_, v) => setNewRatePurpose(v || '')}
                                            placeholder="např. Rozvoz"
                                            styles={{ root: { flex: '2 1 180px' } }}
                                        />
                                        <Dropdown
                                            label="Typ sazby"
                                            options={rateTypeOptions}
                                            selectedKey={newRateType}
                                            onChange={(_, o) => setNewRateType((o?.key as 'Taxi' | 'Service' | 'Personal' | 'Contract') ?? 'Taxi')}
                                            styles={{ root: { flex: '1 1 160px' } }}
                                        />
                                    </Stack>
                                    {newRateType === 'Taxi' && (
                                        <Stack horizontal tokens={{ childrenGap: 24 }} wrap>
                                            <TextField
                                                label="Denní sazba (Kč/km)"
                                                value={newRateDayRate}
                                                onChange={(_, v) => setNewRateDayRate(v || '')}
                                                placeholder="0.00"
                                                type="number"
                                                styles={{ root: { flex: '1 1 140px' } }}
                                            />
                                            <TextField
                                                label="Noční sazba (Kč/km)"
                                                value={newRateNightRate}
                                                onChange={(_, v) => setNewRateNightRate(v || '')}
                                                placeholder="0.00"
                                                type="number"
                                                styles={{ root: { flex: '1 1 140px' } }}
                                            />
                                        </Stack>
                                    )}
                                    <Stack horizontal tokens={{ childrenGap: 24 }} verticalAlign="center" wrap>
                                        <PrimaryButton
                                            text={addingRate ? 'Přidávám…' : 'Přidat sazbu'}
                                            iconProps={addingRate ? undefined : { iconName: 'Add' }}
                                            onRenderText={addingRate ? () => (
                                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                                    <Spinner size={SpinnerSize.small} />
                                                    <span>Přidávám…</span>
                                                </Stack>
                                            ) : undefined}
                                            onClick={handleAddRate}
                                            disabled={addingRate}
                                            styles={{ root: { minWidth: 150, alignSelf: 'flex-end' } }}
                                        />
                                    </Stack>
                                </Stack>
                            </div>

                            {/* List card */}
                            <div style={listCardStyle}>
                                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: 12 }}>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>Sazby</Text>
                                        <span style={countBadgeStyle}>{rates.length}</span>
                                    </Stack>
                                    <DefaultButton
                                        text="Obnovit"
                                        iconProps={{ iconName: 'Refresh' }}
                                        onClick={fetchRates}
                                    />
                                </Stack>

                                <DetailsList
                                    items={rates}
                                    columns={rateColumns}
                                    layoutMode={DetailsListLayoutMode.justified}
                                    selectionMode={SelectionMode.none}
                                    isHeaderVisible={true}
                                    getKey={(item: Rate) => item.id}
                                />
                                {rates.length === 0 && !loading && (
                                    <Stack verticalAlign="center" horizontalAlign="center" style={{ padding: '32px 0' }} tokens={{ childrenGap: 8 }}>
                                        <Icon iconName="Money" style={{ fontSize: 32, color: fluent.palette.neutralTertiaryAlt }} />
                                        <Text style={{ color: fluent.palette.neutralSecondary }}>Žádné sazby</Text>
                                    </Stack>
                                )}
                            </div>
                        </PivotItem>

                    </Pivot>
                )}
            </div>

            {/* Logbook check confirmation */}
            <ConfirmationDialog
                isVisible={isDialogVisible}
                envelopeId={dialogContent.id}
                finalPrice={dialogContent.finalPrice}
                onConfirm={() => { dialogResolve(true); setIsDialogVisible(false); }}
                onCancel={() => { dialogResolve(false); setIsDialogVisible(false); }}
            />

            {/* User delete confirmation */}
            <Dialog
                hidden={!deleteConfirmUser}
                onDismiss={() => setDeleteConfirmUser(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Smazat uživatele?',
                    subText: `Opravdu chcete smazat uživatele ${deleteConfirmUser}?`,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Smazat" onClick={() => deleteConfirmUser && handleDeleteUser(deleteConfirmUser)} />
                    <DefaultButton text="Zrušit" onClick={() => setDeleteConfirmUser(null)} />
                </DialogFooter>
            </Dialog>
            {/* Admin toggle confirmation */}
            <Dialog
                hidden={!adminToggleConfirmUser}
                onDismiss={() => setAdminToggleConfirmUser(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: adminToggleConfirmUser?.isAdmin ? 'Odebrat admin oprávnění?' : 'Udělit admin oprávnění?',
                    subText: adminToggleConfirmUser?.isAdmin
                        ? `Opravdu chcete odebrat admin oprávnění uživateli ${adminToggleConfirmUser?.email}?`
                        : `Opravdu chcete udělit admin oprávnění uživateli ${adminToggleConfirmUser?.email}?`,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Potvrdit" onClick={confirmToggleAdmin} />
                    <DefaultButton text="Zrušit" onClick={() => setAdminToggleConfirmUser(null)} />
                </DialogFooter>
            </Dialog>
            {/* Rate delete confirmation */}
            <Dialog
                hidden={!deleteConfirmRate}
                onDismiss={() => setDeleteConfirmRate(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Smazat sazbu?',
                    subText: `Opravdu chcete smazat sazbu "${rates.find(r => r.id === deleteConfirmRate)?.purpose ?? ''}"?`,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Smazat" onClick={() => deleteConfirmRate && handleDeleteRate(deleteConfirmRate)} />
                    <DefaultButton text="Zrušit" onClick={() => setDeleteConfirmRate(null)} />
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default Admin;
